import { useCallback, useEffect, useState } from "react";
import type { Answer, AskMode } from "../api";

/** ChatTurn is one question and the answer it got. */
export interface ChatTurn {
  question: string;
  answer: Answer;
  /**
   * The chapter the turn was asked in and when, for `recentFor` (ADR-0156):
   * the chapter is what lets the server place the line against a spoiler-free
   * reach, and the time orders turns across lines. A turn stored before these
   * existed has neither, and is never sent as recent.
   */
  chapter_id?: string;
  asked_at?: number;
}

/**
 * Threads are a book's conversations, one per line: the text of the line the
 * chat was opened about, mapped to its turns — or, for a question with no
 * line, the chapter's key (`threadKey`). A "" thread is one stored before
 * that, and is only ever sent as recent.
 */
export type Threads = Record<string, ChatTurn[]>;

/**
 * Modes are the answer mode each conversation is in (ADR-0071), by the same
 * line key as its turns. A line with no entry is in the book's remembered mode
 * (ADR-0160), and spoiler-free until the reader has chosen one.
 *
 * Kept beside the turns rather than inside them: the mode is the
 * conversation's — what the next question will be asked in — while each turn
 * already records the mode it was *answered* in, on the answer itself. Putting
 * one in the other would make "what happens next" and "what happened" the same
 * field.
 */
export type Modes = Record<string, AskMode>;

/**
 * How many earlier turns travel with a question. The server keeps at most
 * eight (ADR-0022), so sending more is bytes it throws away.
 */
export const SENT_TURNS = 8;

/** How many turns one line's conversation remembers, for scrolling back. */
export const KEPT_TURNS = 40;

/** historyFor is a conversation as the ask endpoint takes it. */
export function historyFor(turns: ChatTurn[]): { question: string; answer: string }[] {
  return turns.slice(-SENT_TURNS).map((t) => ({ question: t.question, answer: t.answer.answer }));
}

/**
 * How many turns about other lines travel with a question (ADR-0156). The
 * server keeps at most four, inside its own rune bound.
 */
export const SENT_RECENT = 4;

/**
 * threadKey is which conversation a question belongs to: its line, or, with
 * none, the chapter it was asked from (ADR-0176). A lineless question is about
 * the story up to the reader's place, so it never joins a line's conversation,
 * and one chapter's never follows the reader into the next — where an earlier
 * "what you have read does not say" anchored the answer (2026-10-11).
 */
export function threadKey(line: string, chapterID: string): string {
  return line || `@${chapterID}`;
}

/**
 * isPlaceKey says whether a thread is a chapter's lineless conversation (or
 * one stored with no line before them) rather than a line's. A book's
 * sentence opening with "@" would be taken for one; none has.
 */
export function isPlaceKey(key: string): boolean {
  return key === "" || key.startsWith("@");
}

/** A turn about another line, as the ask endpoint takes it. */
export interface RecentTurn {
  chapter_id: string;
  line: string;
  question: string;
  answer: string;
}

/**
 * recentFor is what the reader was recently told in conversations other than
 * `key`'s, oldest first (ADR-0156): so the answer does not open by explaining
 * again the speaker and the scene it explained a line ago.
 *
 * Only turns answered in `mode`. The client holds the mode (ADR-0071), and an
 * open answer about another line may say what a spoiler-free question must not
 * be told; the server places the rest against the reach.
 */
export function recentFor(threads: Threads, key: string, mode: AskMode): RecentTurn[] {
  const turns: (RecentTurn & { at: number })[] = [];
  for (const [other, thread] of Object.entries(threads)) {
    if (other === key) continue;
    for (const t of thread) {
      if (!t.chapter_id || t.asked_at === undefined || !t.answer.answer || t.answer.reason)
        continue;
      if ((t.answer.mode ?? "spoiler_free") !== mode) continue;
      turns.push({
        chapter_id: t.chapter_id,
        // A lineless turn goes with no line; the server names its chapter.
        line: isPlaceKey(other) ? "" : other,
        question: t.question,
        answer: t.answer.answer,
        at: t.asked_at,
      });
    }
  }
  turns.sort((a, b) => a.at - b.at);
  return turns.slice(-SENT_RECENT).map(({ at: _at, ...t }) => t);
}

/** withTurn adds a turn to one line's conversation and leaves the rest alone. */
export function withTurn(threads: Threads, line: string, turn: ChatTurn): Threads {
  return { ...threads, [line]: [...(threads[line] ?? []), turn].slice(-KEPT_TURNS) };
}

/** withoutLine forgets one line's conversation. */
export function withoutLine(threads: Threads, line: string): Threads {
  const next = { ...threads };
  delete next[line];
  return next;
}

/**
 * parseThreads reads what session storage held, keeping only well-formed
 * turns. Anything else — including the single-conversation list this replaced
 * — reads as no conversations rather than as a broken sheet.
 */
export function parseThreads(raw: string | null): Threads {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw ?? "{}");
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  const out: Threads = {};
  for (const [line, turns] of Object.entries(parsed)) {
    if (!Array.isArray(turns)) continue;
    const valid = turns.filter(
      (t): t is ChatTurn =>
        !!t &&
        typeof t.question === "string" &&
        typeof t.answer?.answer === "string" &&
        Array.isArray(t.answer?.citations),
    );
    if (valid.length) out[line] = valid;
  }
  return out;
}

/**
 * parseModes reads the stored modes, keeping only the ones this version
 * offers. A mode written by a later version, or anything else, reads as
 * spoiler-free by being dropped — the same way the server treats a mode it does
 * not recognise, and for the same reason: failing open would tell a reader more
 * than they asked for.
 */
export function parseModes(raw: string | null): Modes {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw ?? "{}");
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  const out: Modes = {};
  for (const [line, mode] of Object.entries(parsed)) {
    if (mode === "open" || mode === "spoiler_free") out[line] = mode;
  }
  return out;
}

/**
 * parseMode reads the remembered mode: open, or spoiler-free for anything
 * else, failing closed like parseModes.
 */
export function parseMode(raw: string | null): AskMode {
  return raw === "open" ? "open" : "spoiler_free";
}

/**
 * modeOf is the mode the next question on [line] is asked in: the thread's own,
 * or for a thread nobody chose one for, the book's remembered one (ADR-0160).
 */
export function modeOf(modes: Modes, last: AskMode, line: string): AskMode {
  return modes[line] ?? last;
}

/**
 * withAskedMode pins a thread to the mode its first answer came in, so a later
 * switch on another line — which moves the remembered default — does not
 * change the mode of a conversation already under way.
 */
export function withAskedMode(modes: Modes, line: string, mode: AskMode | undefined): Modes {
  if (modes[line] || (mode !== "open" && mode !== "spoiler_free")) return modes;
  return { ...modes, [line]: mode };
}

const storageKey = (key: string) => `sageleaf.chats.${key}`;
const modesKey = (key: string) => `sageleaf.chatmodes.${key}`;
/**
 * The mode a reader last chose on this book (ADR-0160). Local storage, not
 * session: it is a preference like the reading settings and outlives the tab,
 * where the conversations themselves end with it.
 */
const lastModeKey = (key: string) => `sageleaf.chatmode.${key}`;

function loadLastMode(key: string): AskMode {
  try {
    return parseMode(localStorage.getItem(lastModeKey(key)));
  } catch {
    return "spoiler_free";
  }
}

function load(key: string): Threads {
  try {
    return parseThreads(sessionStorage.getItem(storageKey(key)));
  } catch {
    return {};
  }
}

function loadModes(key: string): Modes {
  try {
    return parseModes(sessionStorage.getItem(modesKey(key)));
  } catch {
    return {};
  }
}

/**
 * useThreads is a reader's conversations about one book, one per line.
 *
 * Per line rather than one for the book (ADR-0022, amended): long-pressing a
 * new line is starting a new topic, the chat mark on a line promises the
 * conversation about *that* line, and a single running chat sent the model
 * turns about other sentences with every follow-up — context that pulled its
 * answer off topic and was paid for as input each time.
 *
 * Still grouped by book, not chapter, so following a citation into another
 * chapter keeps them. Held in session storage: they survive that navigation and
 * a reload, and end with the tab. The server holds none of it.
 */
export function useThreads(key: string) {
  const fresh = (key: string) => ({
    key,
    threads: load(key),
    modes: loadModes(key),
    last: loadLastMode(key),
  });
  const [state, setState] = useState(() => fresh(key));
  // A different book is a different set of conversations. Adjusting state
  // during render rather than in an effect, so the old book's never paint.
  if (state.key !== key) setState(fresh(key));
  const threads = state.key === key ? state.threads : {};
  const modes = state.key === key ? state.modes : {};
  const last = state.key === key ? state.last : "spoiler_free";

  useEffect(() => {
    try {
      if (Object.keys(state.threads).length === 0) {
        sessionStorage.removeItem(storageKey(state.key));
      } else {
        sessionStorage.setItem(storageKey(state.key), JSON.stringify(state.threads));
      }
      if (Object.keys(state.modes).length === 0) {
        sessionStorage.removeItem(modesKey(state.key));
      } else {
        sessionStorage.setItem(modesKey(state.key), JSON.stringify(state.modes));
      }
      if (state.last === "open") {
        localStorage.setItem(lastModeKey(state.key), state.last);
      } else {
        localStorage.removeItem(lastModeKey(state.key));
      }
    } catch {
      // Storage full or disabled: the conversations live in memory only.
    }
  }, [state]);

  const add = useCallback((line: string, turn: ChatTurn) => {
    setState((prev) => ({
      ...prev,
      threads: withTurn(prev.threads, line, turn),
      modes: withAskedMode(prev.modes, line, turn.answer.mode),
    }));
  }, []);

  const clear = useCallback((line: string) => {
    setState((prev) => ({ ...prev, threads: withoutLine(prev.threads, line) }));
  }, []);

  // A switch changes this thread and the book's default for the next one
  // (ADR-0160); threads already in a mode keep it.
  const setMode = useCallback((line: string, mode: AskMode) => {
    setState((prev) => ({ ...prev, modes: { ...prev.modes, [line]: mode }, last: mode }));
  }, []);

  return {
    /** Every line's conversation, for what travels as recent (ADR-0156). */
    threads,
    turnsFor: (line: string): ChatTurn[] => threads[line] ?? [],
    /** The mode the next question on this line will be asked in. */
    modeFor: (line: string): AskMode => modeOf(modes, last, line),
    /** Lines that have a conversation. */
    lines: Object.keys(threads).filter((key) => !isPlaceKey(key)),
    add,
    clear,
    setMode,
  };
}

export type ThreadStore = ReturnType<typeof useThreads>;
